import * as Sentry from '@sentry/react';
import type { User } from '@/store/user/types';

export const setSentryUser = (user: User | null) => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.userUuid,
    email: user.email,
    username: user.fullName,
  });

  Sentry.setContext('user_details', {
    subscriptionLevel: user.subscriptionLevel,
    subDomain: user.subDomain,
    isTrial: user.isTrial,
    isAdmin: user.isAdmin,
    isSubAccount: user.isSubAccount,
    emailVerified: user.emailVerified,
  });
};

export const setSentryPageContext = (pageId: string | null, pageName?: string | null) => {
  if (!pageId) {
    Sentry.setContext('page', null);
    return;
  }

  Sentry.setContext('page', {
    pageId,
    pageName: pageName || 'Unknown',
  });
};

export const addSentryBreadcrumb = (
  message: string,
  data?: Record<string, unknown>,
  category = 'user-action',
) => {
  Sentry.addBreadcrumb({
    category,
    message,
    level: 'info',
    data,
  });
};

export const captureSentryException = (
  error: Error,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
  },
) => {
  Sentry.captureException(error, {
    tags: context?.tags,
    extra: context?.extra,
    level: context?.level,
  });
};

export const captureSentryMessage = (
  message: string,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
    level?: Sentry.SeverityLevel;
  },
) => {
  if (context?.tags || context?.extra) {
    Sentry.withScope((scope) => {
      if (context.tags) {
        Object.entries(context.tags).forEach(([key, value]) => {
          scope.setTag(key, value);
        });
      }
      if (context.extra) {
        Object.entries(context.extra).forEach(([key, value]) => {
          scope.setExtra(key, value);
        });
      }
      Sentry.captureMessage(message, context.level || 'info');
    });
  } else {
    Sentry.captureMessage(message, context?.level || 'info');
  }
};
